import "App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "context/AuthContext";
import Sidebar from "components/Sidebar";
import Dashboard from "pages/Dashboard";
import Profiles from "pages/Profiles";
import Campaigns from "pages/Campaigns";
import CampaignDetail from "pages/CampaignDetail";
import Contacts from "pages/Contacts";
import Templates from "pages/Templates";
import Settings from "pages/Settings";
import AntiDetect from "pages/AntiDetect";
import ManualSend from "pages/ManualSend";
import Reports from "pages/Reports";
import RCSFilter from "pages/RCSFilter";
import Replies from "pages/Replies";
import Blacklist from "pages/Blacklist";
import HealthMonitor from "pages/HealthMonitor";
import Login from "pages/Login";
import RequestAccess from "pages/RequestAccess";
import Admin from "pages/Admin";
import Landing from "pages/Landing";
import Changelog from "pages/Changelog";
import WhatsAppGroups from "pages/WhatsAppGroups";
import WhatsAppStatus from "pages/WhatsAppStatus";
import PrivacyPolicy from "pages/PrivacyPolicy";
import TermsOfService from "pages/TermsOfService";
import DesktopApp from "pages/DesktopApp";
import ApiDocs from "pages/ApiDocs";
import { Toaster } from "sonner";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/landing" replace />;
  }
  
  return children;
}

function AppRoutes() {
  const { user, loading, error } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  // Show auth error if present
  if (error && !user) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-red-500/20 rounded-xl p-6 max-w-md text-center">
          <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Error de Autenticacion</h2>
          <p className="text-zinc-400 mb-4 text-sm">{error}</p>
          <div className="flex flex-col gap-2">
            <button 
              onClick={() => { localStorage.removeItem('session_token'); window.location.href = '/landing'; }}
              className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-lg font-medium transition-colors text-sm"
            >
              Reintentar Login
            </button>
            <button 
              onClick={() => window.location.reload()}
              className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-6 py-2 rounded-lg font-medium transition-colors text-sm"
            >
              Recargar Pagina
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/landing" element={<Landing />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/request-access" element={<RequestAccess />} />
      
      {/* Protected routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <div className="flex min-h-screen bg-[#09090B]">
              <Sidebar />
              <main className="flex-1 lg:ml-64 p-4 pt-14 lg:pt-6 lg:p-8 overflow-y-auto min-h-screen">
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/profiles" element={<Profiles />} />
                  <Route path="/campaigns" element={<Campaigns />} />
                  <Route path="/campaigns/:id" element={<CampaignDetail />} />
                  <Route path="/contacts" element={<Contacts />} />
                  <Route path="/templates" element={<Templates />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/antidetect" element={<AntiDetect />} />
                  <Route path="/manual-send" element={<ManualSend />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/rcs-filter" element={<RCSFilter />} />
                  <Route path="/replies" element={<Replies />} />
                  <Route path="/wa-groups" element={<WhatsAppGroups />} />
                  <Route path="/wa-status" element={<WhatsAppStatus />} />
                  <Route path="/blacklist" element={<Blacklist />} />
                  <Route path="/health" element={<HealthMonitor />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/changelog" element={<Changelog />} />
                  <Route path="/desktop-app" element={<DesktopApp />} />
                  <Route path="/developers" element={<ApiDocs />} />
                </Routes>
              </main>
            </div>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster theme="dark" position="bottom-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
